import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

/** Artist dashboard card — shows the artist's art-center membership state:
 *  active membership, pending invites/requests, or a browse CTA. */
export const ArtistCenterCard = () => {
  const [data, setData] = useState(null); // {active, pending}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/memberships/mine");
      setData(d);
    } catch (_e) {
      /* non-blocking */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;

  const invites = data.pending.filter((m) => m.initiated_by === "center");
  const requests = data.pending.filter((m) => m.initiated_by === "artist");

  return (
    <div data-testid="artist-center-card" className="mt-12 border border-[#E5E0D8] bg-white p-6">
      <div className="flex items-center gap-3">
        <Building2 className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.5} />
        <p className="label-eyebrow text-[#1E3A8A]">— Art Hub membership</p>
      </div>

      {data.active ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-serif-display text-2xl">
              Member of{" "}
              <Link
                to={`/art-hub/${data.active.center_id}`}
                data-testid="my-center-link"
                className="text-[#1E3A8A] italic hover:underline"
              >
                {data.active.center_name}
              </Link>
            </p>
            <p className="text-sm text-[#555] mt-1">
              New uploads can be presented under your hub — toggle it on each artwork.
            </p>
          </div>
          <Button
            data-testid="leave-center-btn"
            variant="outline"
            disabled={busy}
            onClick={() => {
              if (!window.confirm(`Leave ${data.active.center_name}? Your works will be detached from the hub.`)) return;
              act(() => api.delete("/memberships/mine/active"), "You left the art hub");
            }}
            className="rounded-none border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white px-5 py-2 h-auto label-eyebrow"
          >
            Leave hub
          </Button>
        </div>
      ) : (
        <>
          {invites.map((m) => (
            <div
              key={m.id}
              data-testid={`center-invite-${m.id}`}
              className="mt-4 flex flex-wrap items-center justify-between gap-4 border-l-2 border-[#F4C430] bg-[#F4EFE6]/40 px-4 py-3"
            >
              <p className="text-sm text-[#333]">
                <span className="font-serif-display text-lg italic text-[#1E3A8A]">{m.center_name}</span>{" "}
                invited you to join as a member artist.
              </p>
              <div className="flex gap-2">
                <Button
                  data-testid={`accept-invite-${m.id}`}
                  disabled={busy}
                  onClick={() => act(() => api.post(`/memberships/${m.id}/respond`, { action: "accept" }), `You joined ${m.center_name}`)}
                  className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-5 py-2 h-auto label-eyebrow"
                >
                  Accept
                </Button>
                <Button
                  data-testid={`decline-invite-${m.id}`}
                  variant="outline"
                  disabled={busy}
                  onClick={() => act(() => api.post(`/memberships/${m.id}/respond`, { action: "decline" }), "Invitation declined")}
                  className="rounded-none border-[#555] text-[#555] px-5 py-2 h-auto label-eyebrow"
                >
                  Decline
                </Button>
              </div>
            </div>
          ))}
          {requests.map((m) => (
            <div
              key={m.id}
              data-testid={`center-request-${m.id}`}
              className="mt-4 flex flex-wrap items-center justify-between gap-4"
            >
              <p className="text-sm text-[#555]">
                Your request to join{" "}
                <span className="font-serif-display italic text-[#1E3A8A]">{m.center_name}</span> is pending review.
              </p>
              <Button
                data-testid={`cancel-request-${m.id}`}
                variant="outline"
                disabled={busy}
                onClick={() => act(() => api.delete(`/memberships/${m.id}`), "Request cancelled")}
                className="rounded-none border-[#555] text-[#555] px-5 py-2 h-auto label-eyebrow"
              >
                Cancel request
              </Button>
            </div>
          ))}
          {data.pending.length === 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm text-[#555]">
                Belong to a community studio or collective? Join your mother body so your
                works appear under its banner.
              </p>
              <Link
                to="/art-hubs"
                data-testid="browse-centers-link"
                className="label-eyebrow border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors px-5 py-2.5"
              >
                Browse art hubs
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
};
